import type { DateTime } from "luxon";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useAnalytics } from "@/contexts/analytics/hooks";
import { useChosenDepartment } from "@/contexts/ChosenDepartment";
import { useCloseOpenShiftSuggestion } from "@/contexts/CloseOpenShiftSuggestionProvider";
import { useDraftMode } from "@/contexts/DraftModeContext";
import { FEATURE_FLAGS } from "@/contexts/FeatureFlagsProvider";
import { useRequestsContext } from "@/contexts/RequestsContext";
import { useSnackbar } from "@/contexts/SnackbarContext";
import { useDepartmentFlags } from "@/hooks/useDepartmentFlags";
import { useErrorSnackbar } from "@/hooks/useErrorSnackbar";
import type { AddNursesEntry, useHandleNurseChange } from "@/hooks/useHandleNurseChange";
import { useJobs } from "@/hooks/useJobs";
import { useLastNameFirst } from "@/hooks/useNameFormat";
import { useNurseTimeOff } from "@/hooks/useNurseTimeOff";
import { useFeatureFlag } from "@/hooks/useOpenFeatureFlag";
import { usePrepareNurseShift } from "@/hooks/usePrepareNurseShift";
import { useScheduleSaves } from "@/hooks/useScheduleSaves";
import { scheduleSlotDay } from "@/utils/departmentWeeks";
import { type Nurse, type NurseData, NurseInShiftCreationSource, type OpenShiftRequest, type WeekData } from "@/utils/models";
import type { ShiftAvailabilityWindow } from "@/utils/shiftAvailability";
import { useDismissOverstaffingWarning } from "@/utils/warningDialogs/useDismissOverstaffingWarning";
import { useStaffingTargetCheck } from "@/utils/warningDialogs/useStaffingTargetCheck";
import { DIFF_HOURS_WARNING } from "@/utils/warningDialogs/utils";
import {
	ADD_STAFF_ANALYTICS_SOURCE,
	buildAddNurseEventProps,
	buildPreparedShift,
	collectRedundantOpenShifts,
	shouldOfferRedundantOpenShift,
	toAddStaffTarget,
} from "./addStaffPublish";
import { formatShiftDate, formatShiftHours, type ShiftAssignment } from "./addStaffUtils";
import { buildMultiAddReview, hasMultiAddWarnings, type MultiAddReview } from "./multiAddReview";

type NurseChange = ReturnType<typeof useHandleNurseChange>;

/** Everything a multi-add needs — the same inputs the single add reads, for N picked staff. */
export type MultiAddRequest = {
	/** The picked staff, in pick order (always 2 or more). */
	nurses: Nurse[];
	/** Their grid rows — predicted overtime / daily hours for the combined review. */
	rows: NurseData[];
	window: ShiftAvailabilityWindow;
	staffingDate: DateTime;
	/** The panel's schedule weeks; `weekIndex`, `dayNo` and `shiftNo` are positions in them. */
	weeks: WeekData[];
	shiftNo: number;
	dayNo: number;
	weekIndex: number;
	/** `""` = no location. */
	room: string;
	/** The step-1 job; `undefined` in role-mode departments. */
	jobId: string | undefined;
	/** Charge / training status from the "Shift details" row — applied to every picked nurse. */
	assignment: ShiftAssignment;
	isActual: boolean;
	skipRedundantOpenShiftSuggestion: boolean;
	setIsAdd: (value: boolean) => void;
	/** Closes the panel — only after a successful add; a failed one keeps the panel and the picks. */
	close: () => void;
};

/** The review waiting on the scheduler: what it found, and the add it gates. */
export type PendingMultiAdd = { request: MultiAddRequest; review: MultiAddReview };

export type MultiAddSubmit = {
	/** Warnings → the combined review; none → one publish with N inserts straight away. */
	submit: (request: MultiAddRequest) => Promise<void>;
	isSubmitting: boolean;
	/** The open review, or `null`. */
	pending: PendingMultiAdd | null;
	/** "Back": close the review, keep the picks. */
	cancelReview: () => void;
	/** "Add {n} to shift" in the review. `dismissTargetWarning` = the "don't show again" box was ticked. */
	confirmReview: (dismissTargetWarning: boolean) => void;
};

const toTarget = (request: MultiAddRequest) => toAddStaffTarget(request, request.window);

/** The batch's own lifecycle events (`_start` / `_success` / `_error`), beside the per-nurse "Staffing - add nurse". */
const MULTI_ADD_EVENT = "add_staff_multi_add";

/** Every picked nurse's warnings plus ONE staffing-target check for all N — the review's content. */
const useBuildMultiAddReview = () => {
	const { chosenDepartment } = useChosenDepartment();
	const getTargetCheck = useStaffingTargetCheck();
	const lastNameFirst = useLastNameFirst();
	const timeOffOf = useNurseTimeOff();
	return (request: MultiAddRequest): MultiAddReview =>
		buildMultiAddReview({
			nurses: request.nurses,
			rows: request.rows,
			window: request.window,
			staffingDate: request.staffingDate,
			dayStart: chosenDepartment.dayStart,
			timeOffOf,
			departmentId: chosenDepartment.department_id,
			isActual: request.isActual,
			// DEFAULT: the same long-day limit the single add's daily-hours warning falls back to (useNurseWarnings).
			longShiftLimitHours: chosenDepartment.longShiftDuration ?? DIFF_HOURS_WARNING,
			target: getTargetCheck({
				weeks: request.weeks,
				locationName: request.room,
				weekIndex: request.weekIndex,
				dayNo: request.dayNo,
				shiftIndex: request.shiftNo,
				isActual: request.isActual,
				addCount: request.nurses.length,
			}),
			lastNameFirst,
		});
};

/** One shift record per picked nurse, built exactly like the single add's. */
const usePrepareEntries = () => {
	const prepareNurseShift = usePrepareNurseShift();
	const enabledJobs = useJobs();
	return (request: MultiAddRequest): AddNursesEntry[] => {
		const target = toTarget(request);
		const deps = { weeks: request.weeks, prepareNurseShift, enabledJobs };
		return request.nurses.map((nurse) => ({
			nurse: buildPreparedShift(deps, target, nurse),
			...scheduleSlotDay(request.weeks, { weekIndex: request.weekIndex, shiftIndex: request.shiftNo, dayIndex: request.dayNo }),
			location: request.room,
		}));
	};
};

/** After a published batch: the per-nurse "Staffing - add nurse" events, one snackbar, fresh requests. */
const useAnnounceMultiAdd = () => {
	const { t } = useTranslation();
	const { track } = useAnalytics();
	const { showSnackbar } = useSnackbar();
	const { refreshRequests } = useRequestsContext();
	return (request: MultiAddRequest) => {
		request.setIsAdd(true);
		const target = toTarget(request);
		const date = scheduleSlotDay(request.weeks, { weekIndex: request.weekIndex, shiftIndex: request.shiftNo, dayIndex: request.dayNo }).date.toFormat(
			"MM/DD/yyyy",
		);
		const count = request.nurses.length;
		for (const nurse of request.nurses) {
			track("Staffing - add nurse", buildAddNurseEventProps({ target, date, nurse, selectedCount: count, batchSize: count }));
		}
		const where = { date: formatShiftDate(request.staffingDate), hours: formatShiftHours(request.window) };
		showSnackbar(t("add.staff.added.count", { count, ...where }), "success");
		refreshRequests();
	};
};

/**
 * Publish all N in ONE `addNurses` call (drafts: local only), mirror each locally, then one snackbar and
 * one analytics event per nurse. Resolves `true` on success; a failure is tracked, shown, and resolves `false`.
 */
const useMultiAddPublish = (addNurses: NurseChange["addNurses"]) => {
	const { t } = useTranslation();
	const { track, trackError } = useAnalytics();
	const { addNurseToShift } = useScheduleSaves();
	const showErrorSnackbar = useErrorSnackbar();
	const { isDraftMode } = useDraftMode();
	const prepareEntries = usePrepareEntries();
	const announce = useAnnounceMultiAdd();

	return async (request: MultiAddRequest): Promise<boolean> => {
		const eventProps = { source: ADD_STAFF_ANALYTICS_SOURCE, batch_size: request.nurses.length, is_actual: request.isActual, is_draft: isDraftMode };
		let entries: AddNursesEntry[] = [];
		track(`${MULTI_ADD_EVENT}_start`, eventProps);
		try {
			entries = prepareEntries(request);
			if (!isDraftMode) {
				await addNurses({ entries, isActual: request.isActual, source: NurseInShiftCreationSource.AddNurse });
				announce(request);
			}
			// Draft mode: the local changes are the whole add — draft tracking picks them up from hospital data.
			for (const entry of entries) addNurseToShift(entry.nurse, entry.date, entry.shiftType, request.isActual, isDraftMode);
			track(`${MULTI_ADD_EVENT}_success`, eventProps);
			return true;
		} catch (e) {
			trackError(`${MULTI_ADD_EVENT}_error`, { ...eventProps, error_message: e instanceof Error ? e.message : String(e) });
			showErrorSnackbar(t("add.staff.error"), { nurses: entries.map((entry) => entry.nurse) }, e);
			return false;
		}
	};
};

/** The redundant-open-shift offers for a multi-add: resolved before it, shown one at a time after it. */
const useRedundantOpenShiftOffers = () => {
	const { chosenDepartment } = useChosenDepartment();
	const { isHospital } = useDepartmentFlags();
	const { isDraftMode } = useDraftMode();
	const { staffRequests } = useRequestsContext();
	const jobManagementEnabled = useFeatureFlag(FEATURE_FLAGS.JobManagementEnabled);
	const suggestCloseOpenShift = useCloseOpenShiftSuggestion();

	const collect = (request: MultiAddRequest): OpenShiftRequest[] => {
		const gate = { skipRedundantOpenShiftSuggestion: request.skipRedundantOpenShiftSuggestion, isActual: request.isActual, isDraftMode, isHospital };
		if (!shouldOfferRedundantOpenShift(gate)) return [];
		return collectRedundantOpenShifts(staffRequests, toTarget(request), request.nurses, { departmentId: chosenDepartment.department_id, jobManagementEnabled });
	};
	// The prompt holds one request at a time and resolves when the scheduler answers, so offers run in turn.
	// A request is listed once per slot the batch fills; once the scheduler keeps it open (or it couldn't
	// change), asking again about the same request would only nag, so its later slots are skipped.
	const offer = async (requests: readonly OpenShiftRequest[]) => {
		const keptOpen = new Set<string>();
		for (const request of requests) {
			if (keptOpen.has(request.id)) continue;
			const closed = await suggestCloseOpenShift({ request, source: ADD_STAFF_ANALYTICS_SOURCE });
			if (!closed) keptOpen.add(request.id);
		}
	};
	return { collect, offer };
};

/**
 * Multi-add: several staff onto one shift in one publish, behind ONE combined review of every warning.
 *
 * Takes the panel's own `useHandleNurseChange()` result so the publish runs on the same mutation owner the
 * footer reads its loading state from (`isLoading.addNurses`).
 */
export const useMultiAddSubmit = (nurseChange: NurseChange): MultiAddSubmit => {
	const buildReview = useBuildMultiAddReview();
	const publish = useMultiAddPublish(nurseChange.addNurses);
	const offers = useRedundantOpenShiftOffers();
	const dismissOverstaffingWarning = useDismissOverstaffingWarning();
	const [pending, setPending] = useState<PendingMultiAdd | null>(null);

	const run = async (request: MultiAddRequest) => {
		const redundant = offers.collect(request);
		const added = await publish(request);
		// The review closes either way; on failure the panel and the picks stay for another try.
		setPending(null);
		if (!added) return;
		await offers.offer(redundant);
		request.close();
	};

	const submit = async (request: MultiAddRequest) => {
		const review = buildReview(request);
		if (hasMultiAddWarnings(review)) {
			setPending({ request, review });
			return;
		}
		await run(request);
	};

	const confirmReview = (dismissTargetWarning: boolean) => {
		if (!pending) return;
		if (dismissTargetWarning) dismissOverstaffingWarning.mutate();
		void run(pending.request);
	};

	return {
		submit,
		isSubmitting: nurseChange.isLoading.addNurses,
		pending,
		cancelReview: () => setPending(null),
		confirmReview,
	};
};
